import { dasherizeParts, FlagState, Stat } from '@timing71/common';
import { parseTime } from '../utils.js';

const makeSectorStat = (index) => ([`S${index + 1}`, 'time', `Sector ${index + 1} time`]);

const getSectorCount = (data) => 1 + parseInt((data.NROFINTERMEDIATETIMES || 4), 10)

const makeColSpec = (sectorCount) => ([
  Stat.NUM,
  Stat.STATE,
  Stat.CLASS,
  Stat.POS_IN_CLASS,
  Stat.DRIVER,
  Stat.TEAM,
  Stat.CAR,
  Stat.LAPS,
  Stat.GAP,
  Stat.INT,
  ...[...new Array(sectorCount)].map((_, i) => makeSectorStat(i)),
  Stat.LAST_LAP,
  Stat.BEST_LAP,
  Stat.PITS
]);

export const getManifest = (data, isNbr) => {
  return {
    colSpec: makeColSpec(getSectorCount(data)),
    name: data.CUP || 'wige Solutions',
    description: dasherizeParts(
      data.TRACKNAME,
      data.HEAT
    ) || 'No active session',
    trackDataSpec: isNbr ? ['Yellow flags', 'At', 'Slow zones', 'At', 'Code 60 zones', 'At'] : []
  }
}

const mapSession = (data, timeData, flagZones) => {

  let flag = FlagState.NONE;

  switch(data.TRACKSTATE) {
    case '0':
      flag = FlagState.GREEN;
      break;

    case '1':
      flag = FlagState.YELLOW;
      break;

    case '2':
      flag = FlagState.RED;
      break;
  }

  let yellows = [];
  let slowZones = [];
  let code60Zones = [];

  Object.values(flagZones || {}).forEach(
    zone => {
      if (zone.zoneType === 60) {
        code60Zones.push(zone.name || zone.post);
      }
      else if (zone.zoneType === 120) {
        slowZones.push(zone.name || zone.post);
      }
      else if (zone.zoneType === 0) {
        yellows.push(zone.name || zone.post);
      }
    }
  );

  if (yellows.length > 0) {
    flag = FlagState.YELLOW;
  }
  if (slowZones.length > 0) {
    flag = FlagState.SLOW_ZONE;
  }
  if (code60Zones.length > 0) {
    flag = FlagState.CODE_60_ZONE;
  }

  const session = {
    flagState: flag,
    trackData: [
      yellows.length,
      [...new Set(yellows)].join(', '),
      slowZones.length,
      [...new Set(slowZones)].join(', '),
      code60Zones.length,
      [...new Set(code60Zones)].join(', ')
    ]
  };

  if (timeData.ENDTIME) {
    const endtime = parseInt(timeData.ENDTIME || 0);
    if (timeData.TIMESTATE === '1') {
      const delta = endtime - Date.now();
      const secondsRemain = Math.floor(delta / 1000) - (timeData.calculatedOffset || 0);
      session.timeRemain = Math.max(secondsRemain, 0);
    }
    else {
      session.timeRemain = endtime / 1000;
      session.pauseClocks = true;
    }
  }

  return session;
}

const CAR_LAP_REGEX = /Lap (?<lap_num>[0-9]+)/i;

const parseGap = (raw) => {
  if (raw === '') {
    return raw;
  }
  if (raw[0] === 'R') {
    const laps = parseInt(raw.slice(1), 10);
    return `${laps} lap${laps === 1 ? '' : 's'}`;
  }
  if (raw[0] === '-') {
    return raw.slice(4);
  }
  try {
    return parseTime(raw);
  }
  catch {
    return raw;
  }
}

const CAR_STATE = {
  '0': 'N/S',
  '1': 'RUN',
  '2': 'RUN',
  '3': 'RUN',
  '4': 'RUN',
  '5': 'RUN',
  '6': 'RUN',
  '7': 'RUN',
  '8': 'RUN',
  '9': 'RUN',
  '10': 'RUN',
  '11': 'RUN',
  '12': 'RUN',
  '14': 'PIT',
  '15': 'OUT',
  '16': 'RUN',
  '17': 'RUN',
  '20': 'PIT'
};

const FIRST_SECTOR_INDEX = 10;

const mapCars = (data) => {
  const cars = [];

  let leaderLap = null;
  let lap = null;

  const fastestLapByClass = {};

  const sectorCount = getSectorCount(data);

  (data.RESULT || []).forEach(
    (raw, index) => {

      let gap = parseGap(raw.GAP);
      const interval = parseGap(raw.INT);
      const hasNewLap = `${gap}`.match(CAR_LAP_REGEX);

      if (hasNewLap) {
        const newLap = parseInt(hasNewLap.groups.lap_num, 10);
        if (leaderLap) {
          const gapLaps = leaderLap - newLap;
          gap = `${gapLaps} lap${gapLaps === 1 ? '' : 's'}`;
        }
        else {
          leaderLap = newLap
        }

        lap = newLap;
      }

      const lastLap = parseTime(raw.LASTLAPTIME);
      const bestLap = parseTime(raw.FASTESTLAP);
      const clazz = raw.CLASSNAME;

      if (bestLap > 0 && (!fastestLapByClass[clazz] || bestLap < fastestLapByClass[clazz].time)) {
        fastestLapByClass[clazz] = {
          time: bestLap,
          index
        };
      }

      const sectors = [];

      for (let i = 0; i < sectorCount; i++) {
        const myTime = parseTime(raw[`S${i + 1}TIME`]);
        const hasBest = `${data.BEST?.[i]?.[0]}` === raw.STNR && parseTime(data.BEST?.[i]?.[1]) === lastLap;
        const lastInter = parseInt(raw.LASTINTERMEDIATENUMBER, 10);
        sectors.push([myTime, hasBest ? 'sb' : i < lastInter - 1 ? 'old' : '']);
      }

      const car = [
        raw.STNR,
        raw.ONTRACK === false ? 'PIT' : CAR_STATE[raw.LASTINTERMEDIATENUMBER] || '?',
        clazz,
        raw.CLASSRANK,
        raw.NAME,
        raw.TEAM,
        raw.CAR,
        lap || raw.LAPS,
        gap,
        interval,
        ...sectors,
        [lastLap, lastLap === bestLap ? 'pb' : ''],
        [bestLap, ''],
        raw.PITSTOPCOUNT
      ];

      cars.push(car);
    }
  );

  if (cars.length > 0) {
    // Leader should have no gap/interval shown
    cars[0][8] = '';
    cars[0][9] = '';
  }

  Object.values(fastestLapByClass).forEach(
    ({ index, time }) => {
      const car = cars[index];
      if (car) {
        car[car.length - 2] = [time, 'sb'];
        if (car[car.length - 2][0] === car[car.length - 3][0] && car[car.length - 4][0]) {
          car[car.length - 3][1] = 'sb-new';
        }
      }
    }
  );

  const bestSectors = data.BEST || [];

  cars.forEach(
    car => {
      for (let sectorIdx = 0; sectorIdx < sectorCount; sectorIdx++) {
        const carSectorIdx = FIRST_SECTOR_INDEX + sectorIdx;
        const raceNum = car[0];
        const bestSector = bestSectors[sectorIdx];
        if (`${bestSector[0]}` === raceNum) {
          const parsedTime = parseTime(bestSector[1]);
          if (parsedTime === car[carSectorIdx][0]) {
            car[carSectorIdx][1] = 'sb';
          }
        }
      }
    }
  )



  return cars;
}

export const translate = (data, messages, timeData, flagZones) => {
  return {
    cars: mapCars(data),
    session: mapSession(data, timeData, flagZones)
  }
}
